import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { client, urlFor } from '../SanityClient';
import ProjectGrid from '../Project-Grid';
import ProjectFilters from '../Project-Filter';

const PAGE_SIZE = 12;

export default function ProjectsIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [clients, setClients] = useState([]);

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const nameFilter = searchParams.get('name') || '';
  const clientFilter = searchParams.get('client') || '';

  const start = (page - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;

  useEffect(() => {
    const clientQuery = `
      array::unique(*[_type == "project" && defined(client)].client) | order(@ asc)
    `;
    client.fetch(clientQuery)
      .then(res => setClients(res || []))
      .catch(e => console.error('Clients fetch error:', e));
  }, []);

  useEffect(() => {
    setLoading(true);

    const filters = [
      `_type == "project"`,
      `!(_id in path("drafts.**"))`,
      nameFilter ? `title match $nameMatch` : '',
      clientFilter ? `client == $client` : '',
    ].filter(Boolean).join(' && ');

    const params = {
      nameMatch: `${nameFilter}*`,
      client: clientFilter || undefined,
      start,
      end,
    };

    const countQuery = `count(*[${filters}])`;

    const pageQuery = `
      *[${filters}]
      | order(coalesce(publishedAt, _updatedAt, _createdAt) desc, title asc)
      [$start...$end]{
        _id,
        title,
        client,
        market,
        description,
        mainImage,
        "slug": coalesce(projectSlug.current, projectSlug)
      }
    `;

    Promise.all([client.fetch(countQuery, params), client.fetch(pageQuery, params)])
      .then(([count, docs]) => {
        const safeCount = typeof count === 'number' ? count : (docs?.length || 0);
        setTotalCount(safeCount);

        const list = (docs || []).map(d => ({
          id: d._id,
          slug: d.slug || '',
          title: d.title,
          imageSrc: d.mainImage
            ? urlFor(d.mainImage).width(1000).height(750).fit('crop').auto('format').url()
            : '',
          client: d.client,
          market: d.market,
          description: d.description,
        }));
        setProjects(list);

      })
      .catch(e => console.error('ProjectsIndex fetch error:', e))
      .finally(() => setLoading(false));
  }, [nameFilter, clientFilter, start, end]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount]
  );

  const goToPage = (p) => {
    const clamped = Math.min(Math.max(1, p), Math.max(1, totalPages));
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', String(clamped));
      return next;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applyFilters = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      if (nameFilter) next.set('name', nameFilter); else next.delete('name');
      if (clientFilter) next.set('client', clientFilter); else next.delete('client');
      return next;
    });
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams({ page: '1' }));
  };

  const setNameFilter = (v) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('name', v);
      return next;
    }, { replace: true });
  };
  const setClientFilter = (v) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('client', v);
      return next;
    }, { replace: true });
  };

  return (
    <div className="hdow-container">
      <h1 className="hdow-title">Our Work</h1>

      <ProjectFilters
        nameFilter={nameFilter}
        onNameChange={setNameFilter}
        clientFilter={clientFilter}
        onClientChange={setClientFilter}
        clients={clients}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      {loading ? (
        <p style={{ padding: '1rem' }}>Loading…</p>
      ) : (
        <>
          <ProjectGrid projects={projects} />

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <nav className="hdow-pagination" role="navigation" aria-label="Projects pagination" style={{ marginTop: 24 }}>
              <button
                className="hdow-page-btn"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                ← Prev
              </button>

              <ul className="hdow-page-list">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const n = i + 1;
                  const isActive = n === page;

                  // concise number list with ellipses
                  const show =
                    n === 1 ||
                    n === totalPages ||
                    Math.abs(n - page) <= 1 ||
                    (page <= 3 && n <= 5) ||
                    (page >= totalPages - 2 && n >= totalPages - 4);

                  if (!show) {
                    if (n === 2 || n === totalPages - 1) {
                      return <li key={`e-${n}`} className="hdow-page-ellipsis">…</li>;
                    }
                    return null;
                  }

                  return (
                    <li key={n}>
                      <button
                        className={`hdow-page-num ${isActive ? 'is-active' : ''}`}
                        onClick={() => goToPage(n)}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {n}
                      </button>
                    </li>
                  );
                })}
              </ul>

              <button
                className="hdow-page-btn"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                Next →
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
